import {
  Book,
  Document,
  Image,
  ChartBarTarget,
  ChartBar,
  Settings,
  Home,
  TaskComplete,
  Building,
  UserMultiple,
  Bookmark,
  UserSettings,
  ChevronRight,
  MachineLearning,
  Chat,
  Catalog,
  Task,
  Idea,
  GameConsole,
  Email,
  Grid,
  TableOfContents,
  Category,
  Template,
  FolderDetails,
  Layers,
  Education,
  ChevronSort,
  User,
  Renew,
  Help,
  Notification,
} from '@carbon/icons-react'
import type { CarbonIconType } from '@carbon/icons-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, memo, useCallback } from 'react'
import { UserRole } from '@sphere/auth/domain'
import styles from './Sidebar.module.scss'

/**
 * Item de navigation dans la sidebar
 */
export interface SidebarItem {
  id: string
  label: string
  icon: CarbonIconType
  href: string
  badge?: string | number
  children?: SidebarItem[]
  exactMatch?: boolean
}

/**
 * Section de navigation
 */
export interface SidebarSection {
  title?: string
  items: SidebarItem[]
}

/**
 * Configuration de navigation par rôle
 */
const navigationConfig: Record<UserRole, SidebarSection[]> = {
  [UserRole.TEACHER]: [
    {
      title: 'Navigation',
      items: [
        {
          id: 'home',
          label: 'Accueil',
          icon: Home,
          href: '/',
          exactMatch: true,
        },
      ],
    },
    {
      title: 'Enseignement',
      items: [
        {
          id: 'my-classes',
          label: 'Mes Classes',
          icon: UserMultiple,
          href: '/classes',
        },
        {
          id: 'my-courses',
          label: 'Mes Cours',
          icon: Book,
          href: '/teachings',
        },
        {
          id: 'content',
          label: 'Contenus Pédagogiques',
          icon: Document,
          href: '/content',
          children: [
            {
              id: 'chapters',
              label: 'Chapitres',
              icon: TableOfContents,
              href: '/chapters',
            },
            {
              id: 'lessons',
              label: 'Leçons',
              icon: TaskComplete,
              href: '/lessons',
            },
            {
              id: 'resources',
              label: 'Ressources',
              icon: Image,
              href: '/resources',
            },
          ],
        },
      ],
    },
    {
      title: 'Activités',
      items: [
        {
          id: 'exercises',
          label: 'Exercices & Évaluations',
          icon: Task,
          href: '/learning/exercises',
        },
      ],
    },
    {
      title: 'Suivi',
      items: [
        {
          id: 'analytics-personal',
          label: 'Activités Personnelles',
          icon: ChartBar,
          href: '/analytics',
        },
        {
          id: 'analytics-establishment',
          label: 'Activités Établissement',
          icon: Building,
          href: '/analytics/establishment',
        },
      ],
    },
    {
      title: 'Communication',
      items: [
        {
          id: 'announcements',
          label: 'Annonces',
          icon: Chat,
          href: '/communication/announcements',
        },
        {
          id: 'emails',
          label: 'Emails',
          icon: Email,
          href: '/communication/emails',
        },
      ],
    },
    {
      title: 'Outils',
      items: [
        { id: 'ai', label: 'IA Studio', icon: MachineLearning, href: '/ai' },
      ],
    },
    {
      items: [
        {
          id: 'profile',
          label: 'Mon Profil',
          icon: User,
          href: '/profile',
        },
        {
          id: 'settings',
          label: 'Paramètres',
          icon: Settings,
          href: '/settings',
        },
        { id: 'help', label: 'Aide & Guide', icon: Help, href: '/docs' },
      ],
    },
  ],
  [UserRole.PEDAGOGICAL_COORDINATOR]: [
    {
      title: 'Navigation',
      items: [{ id: 'home', label: 'Accueil', icon: Home, href: '/' }],
    },
    {
      title: 'Enseignement',
      items: [
        {
          id: 'my-classes',
          label: 'Mes Classes',
          icon: UserMultiple,
          href: '/classes',
        },
        {
          id: 'my-courses',
          label: 'Mes Cours',
          icon: Book,
          href: '/teachings',
        },
      ],
    },
    {
      title: 'Outils',
      items: [
        { id: 'ai', label: 'IA Studio', icon: MachineLearning, href: '/ai' },
      ],
    },
    {
      items: [
        {
          id: 'settings',
          label: 'Paramètres',
          icon: Settings,
          href: '/settings',
        },
        { id: 'help', label: 'Aide & Guide', icon: Help, href: '/docs' },
      ],
    },
  ],
  [UserRole.STUDENT]: [
    {
      title: 'Navigation',
      items: [{ id: 'home', label: 'Accueil', icon: Home, href: '/' }],
    },
    {
      title: 'Apprentissage',
      items: [
        {
          id: 'courses',
          label: 'Mes Cours',
          icon: Book,
          href: '/teachings',
        },
      ],
    },
    {
      title: 'Activités',
      items: [
        {
          id: 'training',
          label: "S'entraîner",
          icon: ChartBarTarget,
          href: '/learning/exercises',
        },
        {
          id: 'in-progress',
          label: 'Mes tentatives en cours',
          icon: Renew,
          href: '/learning/in-progress',
        },
      ],
    },
    {
      title: 'Suivi',
      items: [
        {
          id: 'analytics-personal',
          label: 'Mes Activités',
          icon: ChartBar,
          href: '/analytics',
        },
      ],
    },
    {
      title: 'Communication',
      items: [
        {
          id: 'notifications',
          label: 'Notifications',
          icon: Notification,
          href: '/notifications',
        },
      ],
    },
    {
      title: 'Outils',
      items: [
        { id: 'ai', label: 'IA Studio', icon: MachineLearning, href: '/ai' },
      ],
    },
    {
      items: [
        { id: 'profile', label: 'Mon Profil', icon: User, href: '/profile' },
        {
          id: 'settings',
          label: 'Paramètres',
          icon: Settings,
          href: '/settings',
        },
        { id: 'help', label: 'Aide & Guide', icon: Help, href: '/docs' },
      ],
    },
  ],
  [UserRole.ADMIN]: [
    {
      title: 'Navigation',
      items: [
        {
          id: 'home',
          label: 'Accueil',
          icon: Home,
          href: '/',
          exactMatch: true,
        },
      ],
    },
    {
      title: 'Établissement',
      items: [
        {
          id: 'establishment',
          label: 'Mon Établissement',
          icon: Building,
          href: '/establishment',
        },
        {
          id: 'members',
          label: 'Accès & Membres',
          icon: UserMultiple,
          href: '/members',
        },
        {
          id: 'classes',
          label: 'Classes & Groupes',
          icon: Education,
          href: '/classes',
        },
      ],
    },
    {
      title: 'Enseignement',
      items: [
        {
          id: 'my-courses',
          label: 'Mes Cours',
          icon: Book,
          href: '/teachings',
        },
        {
          id: 'content',
          label: 'Contenus Pédagogiques',
          icon: Document,
          href: '/content',
          children: [
            {
              id: 'chapters',
              label: 'Chapitres',
              icon: TableOfContents,
              href: '/chapters',
            },
            {
              id: 'lessons',
              label: 'Leçons',
              icon: TaskComplete,
              href: '/lessons',
            },
            {
              id: 'resources',
              label: 'Ressources',
              icon: Image,
              href: '/resources',
            },
          ],
        },
      ],
    },
    {
      title: 'Référentiels',
      items: [
        {
          id: 'curriculum-dashboard',
          label: "Vue d'ensemble",
          icon: Grid,
          href: '/curriculum',
          exactMatch: true,
        },
        {
          id: 'programs',
          label: 'Programmes',
          icon: Bookmark,
          href: '/curriculum/programs',
        },
        {
          id: 'competencies',
          label: 'Compétences',
          icon: ChartBarTarget,
          href: '/curriculum/competencies',
        },
        {
          id: 'concepts',
          label: 'Notions',
          icon: Idea,
          href: '/curriculum/concepts',
        },
        {
          id: 'subjects',
          label: 'Matières',
          icon: Catalog,
          href: '/curriculum/subjects',
        },
        {
          id: 'subject-types',
          label: 'Types de Matières',
          icon: Layers,
          href: '/curriculum/subject-types',
        },
        {
          id: 'levels',
          label: 'Niveaux',
          icon: Grid,
          href: '/curriculum/levels',
        },
      ],
    },
    {
      title: 'Learning',
      items: [
        {
          id: 'learning-paths',
          label: 'Parcours Pédagogiques',
          icon: Book,
          href: '/learning/paths',
        },
        {
          id: 'learning-activities',
          label: 'Activités',
          icon: GameConsole,
          href: '/learning/activities',
        },
        {
          id: 'activity-types',
          label: "Types d'Activités",
          icon: Category,
          href: '/learning/types',
        },
        {
          id: 'activity-templates',
          label: "Templates d'Activités",
          icon: Template,
          href: '/learning/templates',
        },
        {
          id: 'activity-categories',
          label: 'Catégories',
          icon: FolderDetails,
          href: '/learning/categories',
        },
      ],
    },
    // {
    //   title: 'Administration',
    //   items: [
    //     {
    //       id: 'users',
    //       label: 'Utilisateurs',
    //       icon: UserSettings,
    //       href: '/admin/users',
    //     },
    //     {
    //       id: 'reports',
    //       label: 'Rapports & Exports',
    //       icon: ChartBar,
    //       href: '/reports',
    //     },
    //   ],
    // },
    {
      title: 'Outils',
      items: [
        { id: 'ai', label: 'IA Studio', icon: MachineLearning, href: '/ai' },
      ],
    },
    {
      title: 'Annonces',
      items: [
        {
          id: 'announcements',
          label: 'Annonces',
          icon: Chat,
          href: '/communication',
        },
        {
          id: 'emails',
          label: 'Emails',
          icon: Email,
          href: '/communication/emails',
        },
      ],
    },
    {
      items: [
        {
          id: 'profile',
          label: 'Mon Profil',
          icon: User,
          href: '/profile',
        },
        {
          id: 'settings',
          label: 'Paramètres',
          icon: Settings,
          href: '/settings',
        },
        { id: 'help', label: 'Aide & Guide', icon: Help, href: '/docs' },
      ],
    },
  ],
  [UserRole.PRINCIPAL]: [
    {
      title: 'Navigation',
      items: [{ id: 'home', label: 'Accueil', icon: Home, href: '/' }],
    },
    {
      title: 'Établissement',
      items: [
        {
          id: 'establishment',
          label: 'Mon Établissement',
          icon: Building,
          href: '/establishment',
        },
        {
          id: 'members',
          label: 'Accès & Membres',
          icon: UserMultiple,
          href: '/members',
        },
        {
          id: 'classes',
          label: 'Classes & Groupes',
          icon: Education,
          href: '/classes',
        },
      ],
    },
    {
      title: 'Enseignement',
      items: [
        {
          id: 'my-courses',
          label: 'Mes Cours',
          icon: Book,
          href: '/teachings',
        },
        {
          id: 'content',
          label: 'Contenus Pédagogiques',
          icon: Document,
          href: '/content',
          children: [
            {
              id: 'chapters',
              label: 'Chapitres',
              icon: TableOfContents,
              href: '/chapters',
            },
            {
              id: 'lessons',
              label: 'Leçons',
              icon: TaskComplete,
              href: '/lessons',
            },
            {
              id: 'resources',
              label: 'Ressources',
              icon: Image,
              href: '/resources',
            },
          ],
        },
      ],
    },
    {
      title: 'Outils',
      items: [
        { id: 'ai', label: 'IA Studio', icon: MachineLearning, href: '/ai' },
      ],
    },
    {
      title: 'Annonces',
      items: [
        {
          id: 'announcements',
          label: 'Annonces',
          icon: Chat,
          href: '/communication',
        },
        {
          id: 'emails',
          label: 'Emails',
          icon: Email,
          href: '/communication/emails',
        },
      ],
    },
    {
      items: [
        {
          id: 'profile',
          label: 'Mon Profil',
          icon: User,
          href: '/profile',
        },
        {
          id: 'settings',
          label: 'Paramètres',
          icon: Settings,
          href: '/settings',
        },
        { id: 'help', label: 'Aide & Guide', icon: Help, href: '/docs' },
      ],
    },
  ],
  // {
  //   title: 'Administration',
  //   items: [
  //     {
  //       id: 'users',
  //       label: 'Utilisateurs',
  //       icon: UserSettings,
  //       href: '/admin/users',
  //     },
  //     {
  //       id: 'reports',
  //       label: 'Rapports & Exports',
  //       icon: ChartBar,
  //       href: '/reports',
  //     },
  //   ],
  // },

  [UserRole.ADMINISTRATIVE_MANAGER]: [
    {
      title: 'Navigation',
      items: [{ id: 'home', label: 'Accueil', icon: Home, href: '/' }],
    },
    {
      title: 'Établissement',
      items: [
        {
          id: 'establishment',
          label: 'Mon Établissement',
          icon: Building,
          href: '/establishment',
        },
        {
          id: 'members',
          label: 'Accès & Membres',
          icon: UserMultiple,
          href: '/members',
        },
        {
          id: 'classes',
          label: 'Classes & Groupes',
          icon: Education,
          href: '/classes',
        },
      ],
    },
    {
      items: [
        {
          id: 'profile',
          label: 'Mon Profil',
          icon: User,
          href: '/profile',
        },
        {
          id: 'settings',
          label: 'Paramètres',
          icon: Settings,
          href: '/settings',
        },
        { id: 'help', label: 'Aide & Guide', icon: Help, href: '/docs' },
      ],
    },
  ],
  [UserRole.SUPPORT]: [
    {
      title: 'Navigation',
      items: [{ id: 'home', label: 'Accueil', icon: Home, href: '/' }],
    },
    {
      title: 'Établissement',
      items: [
        {
          id: 'establishment',
          label: 'Mon Établissement',
          icon: Building,
          href: '/establishment',
        },
        {
          id: 'members',
          label: 'Accès & Membres',
          icon: UserMultiple,
          href: '/members',
        },
      ],
    },
    {
      items: [
        { id: 'profile', label: 'Mon Profil', icon: User, href: '/profile' },
        {
          id: 'settings',
          label: 'Paramètres',
          icon: Settings,
          href: '/settings',
        },
        { id: 'help', label: 'Aide & Guide', icon: Help, href: '/docs' },
      ],
    },
  ],
}

export interface SidebarProps {
  userRole: UserRole
  collapsed?: boolean
  onToggleCollapsed?: () => void
  className?: string
  currentEstablishment?: { id: string; name: string } | null
  availableEstablishments?: Array<{ id: string; name: string }>
  onEstablishmentChange?: (establishmentId: string) => void
}

/**
 * SidebarNavItem - Item de navigation individuel
 */
interface SidebarNavItemProps {
  item: SidebarItem
  collapsed: boolean
  level?: number
}

const SidebarNavItem = memo(function SidebarNavItem({
  item,
  collapsed,
  level = 0,
}: SidebarNavItemProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [expanded, setExpanded] = useState(false)
  const currentPath = location.pathname

  const isChildActive = (children: SidebarItem[]): boolean => {
    return children.some(
      child =>
        currentPath === child.href ||
        currentPath.startsWith(child.href + '/') ||
        (child.children && isChildActive(child.children))
    )
  }

  const hasChildren = item.children && item.children.length > 0

  const isActive = item.exactMatch
    ? currentPath === item.href
    : currentPath === item.href ||
      currentPath.startsWith(item.href + '/') ||
      (hasChildren && isChildActive(item.children || []))

  useEffect(() => {
    if (!collapsed && hasChildren && item.children) {
      if (isChildActive(item.children)) {
        setExpanded(true)
      }
    }
  }, [currentPath, hasChildren, item.children, collapsed])

  const handleClick = useCallback(() => {
    if (hasChildren && !collapsed) {
      setExpanded(prev => !prev)
    } else if (!hasChildren) {
      navigate(item.href)
    }
  }, [hasChildren, navigate, item.href, collapsed])

  const itemClasses = [
    styles.navItem,
    isActive ? styles.navItemActive : '',
    collapsed ? styles.navItemCollapsed : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div>
      <button
        className={itemClasses}
        onClick={handleClick}
        title={collapsed ? item.label : undefined}
      >
        <item.icon size={18} className={styles.navIcon} />

        {!collapsed && (
          <>
            <span className={styles.navLabel}>{item.label}</span>
            {item.badge && (
              <span className={styles.navBadge}>{item.badge}</span>
            )}
            {hasChildren && (
              <ChevronRight
                size={16}
                className={`${styles.navChevron} ${expanded ? styles.navChevronExpanded : ''}`}
              />
            )}
          </>
        )}
      </button>

      {!collapsed && hasChildren && expanded && (
        <div className={styles.navChildren}>
          {item.children?.map(child => (
            <SidebarNavItem
              key={child.id}
              item={child}
              collapsed={collapsed}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
})

/**
 * Sidebar - Panneau latéral de navigation Carbon
 */
export const Sidebar = memo(function Sidebar({
  userRole,
  collapsed = false,
  onToggleCollapsed,
  className,
  currentEstablishment,
  availableEstablishments,
  onEstablishmentChange,
}: SidebarProps) {
  const sections =
    navigationConfig[userRole] || navigationConfig[UserRole.TEACHER] || []
  const navigate = useNavigate()

  const sidebarClasses = [
    styles.sidebar,
    collapsed ? styles.sidebarCollapsed : styles.sidebarExpanded,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <aside className={sidebarClasses}>
      {/* Context selector pour Admin */}
      {!collapsed && userRole === UserRole.ADMIN && (
        <div className={styles.contextSelector}>
          <button
            className={styles.contextButton}
            onClick={() => navigate('/admin/select-establishment')}
          >
            <div className={styles.contextButtonContent}>
              <div className={styles.contextLabel}>Contexte</div>
              <div className={styles.contextValue}>
                {currentEstablishment?.name || 'Sélectionner...'}
              </div>
            </div>
            <ChevronSort size={16} />
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className={styles.nav}>
        {sections.map((section, i) => (
          <div key={i} className={styles.section}>
            {!collapsed && section.title && (
              <div className={styles.sectionTitle}>{section.title}</div>
            )}
            {section.items.map(item => (
              <SidebarNavItem key={item.id} item={item} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.version}>{!collapsed ? 'v1.0.0' : null}</div>
      </div>
    </aside>
  )
})
